/**
 * Cross-app SSO between Wizetok and ReachOut via short-lived handoff JWT.
 */
import { adminAuthAPI } from '../services/api';

/**
 * ReachOut origin (no trailing slash), or '' when not configured.
 * Runtime config (APP_CONFIG.REACHOUT_URL, written by entrypoint.sh from the
 * ConfigMap) with a REACT_APP_REACHOUT_URL fallback for local dev — the same
 * resolution order as API_URL / WS_URL. No host guessing: when it is unset,
 * cross-app SSO simply does not run and the login form is shown.
 */
export function getReachOutBaseUrl() {
  const appConfig = window.APP_CONFIG || {};
  const fromRuntime = (appConfig.REACHOUT_URL || '').trim().replace(/\/$/, '');
  if (fromRuntime) return fromRuntime;

  return (process.env.REACT_APP_REACHOUT_URL || '').trim().replace(/\/$/, '');
}

export function buildReachOutLoginUrl({ handoffToken, email } = {}) {
  const base = `${getReachOutBaseUrl()}/login`;
  const params = new URLSearchParams();
  if (handoffToken) params.set('handoff', handoffToken);
  if (email) params.set('login_hint', email);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/** Same-origin bridge on Wizetok — uses localStorage token, then redirects to ReachOut. */
export function getReachOutBridgeUrl() {
  return `${window.location.origin}/admin/sso/reachout`;
}

/**
 * Open ReachOut in a new tab. Do not open Wizetok /admin/sso/reachout as the
 * tab URL — that keeps the user on Wizetok. ReachOut login then SSO via iframe.
 */
export function openReachOutInNewTab() {
  const base = getReachOutBaseUrl();
  if (!base) return false;
  const url = `${base}/login`;
  const win = window.open(url, "_blank", "noopener,noreferrer");
  if (win) return true;
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  link.remove();
  return false;
}

/** Open ReachOut with token exchange (from Wizetok UI). */
export async function openReachOutSso(email) {
  openReachOutInNewTab();
}

/** Used if bridge page needs a direct mint (also used as fallback). */
export async function mintAndOpenReachOut(email) {
  try {
    const { data } = await adminAuthAPI.createHandoff('reachout');
    const token = data?.handoffToken;
    if (token) {
      window.location.replace(buildReachOutLoginUrl({ handoffToken: token, email }));
      return true;
    }
  } catch (e) {
    console.error('[cross-app] handoff mint failed', e?.response?.status, e?.message || e);
  }
  return false;
}
